import React, { useEffect, useState, useCallback } from "react";
import Table from "@components/table";
import { Transaction } from "@apis/index";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import styled from "@emotion/styled";
import { CheckCircle, AlertCircle, Coins } from "lucide-react";

const AmountText = styled.span<{ positive: boolean }>`
  font-weight: 800;
  font-size: 14px;
  color: ${(props) => (props.positive ? "#10b981" : "#f43f5e")};
  background: ${(props) => (props.positive ? "#10b98115" : "#f43f5e15")};
  padding: 6px 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const PointIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fdf2f8;
  color: #db2777;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoyaltyPoints = ({ customerId }: { customerId: number }) => {
  const { settings } = useGeneralSettings();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await Transaction.list({
        params: {
          limit: 10,
          page: page,
          userId: customerId,
          purpose: "loyalty_reward", // Filter only loyalty points
        },
      });
      setData(res.items || []);
      setTotalPages(res.totalPages || 1);
      setTotalItems(res.totalItems || 0);
    } catch (error) {
      console.error("Failed to fetch loyalty points", error);
    } finally {
      setLoading(false);
    }
  }, [page, customerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      title: "S.N",
      key: "sn",
      width: "5%",
      render: (_: any, index: number) => (
        <span style={{ color: "#64748b", fontWeight: 600 }}>
          {(page - 1) * 10 + index + 1}
        </span>
      ),
    },
    {
      title: "Reward Detail",
      key: "purpose",
      width: "35%",
      render: (row: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <PointIcon>
            <Coins size={16} />
          </PointIcon>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 700, color: "#0f172a" }}>
              {row.purpose || "Loyalty Reward"}
            </span>
            <span style={{ fontSize: "12px", color: "#64748b" }}>
              Log ID: #{row.id}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Points Earned",
      key: "amount",
      width: "25%",
      render: (row: any) => {
        const isCredit = row.type === "credit";
        return (
          <AmountText positive={isCredit}>
            {isCredit ? "+" : "-"} {Helper.formatCurrency(Math.abs(row.amount || 0), settings)}
          </AmountText>
        );
      },
    },
    {
      title: "Transaction Date",
      key: "createdAt",
      width: "20%",
      render: (row: any) => (
        <span style={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>
          {row.createdAt ? new Date(row.createdAt).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }) : "---"}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "15%",
      render: (row: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#10b981", fontWeight: 700, fontSize: "12px" }}>
          <CheckCircle size={14} />
          {row.status || "Completed"}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "0" }}>
      <Table
        columns={columns}
        data={data}
        loading={loading}
        totalItems={totalItems}
        totalPages={totalPages}
        pageControlled={page}
        onPageChange={setPage}
      />
    </div>
  );
};

export default LoyaltyPoints;
